import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FranchiseForm from '../components/FranchiseForm';
import ProductCard from '../components/ProductCard';

// Realistic tea cup SVG component
const TeaCupIcon: React.FC = () => (
  <svg width="56" height="56" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Saucer */}
    <ellipse cx="28" cy="54" rx="22" ry="5" fill="#c4956a" opacity="0.45" />
    <ellipse cx="28" cy="53" rx="20" ry="4" fill="#d4a574" opacity="0.6" />
    {/* Cup body */}
    <path d="M8 22 C8 22 6 48 12 50 C18 52 38 52 44 50 C50 48 48 22 48 22 Z" fill="url(#cupGrad)" stroke="#8B3A3A" strokeWidth="1.5" />
    {/* Tea liquid */}
    <path d="M12 28 C12 28 11 44 15 46 C20 48 36 48 41 46 C45 44 44 28 44 28 Z" fill="url(#teaGrad)" opacity="0.85" />
    {/* Cup rim highlight */}
    <ellipse cx="28" cy="22" rx="20" ry="4.5" fill="url(#rimGrad)" stroke="#8B3A3A" strokeWidth="1.2" />
    {/* Handle */}
    <path d="M48 28 C56 28 58 36 56 40 C54 44 50 44 48 40" fill="none" stroke="#8B3A3A" strokeWidth="2.5" strokeLinecap="round" />
    {/* Steam */}
    <path d="M20 16 C20 12 24 12 22 8" stroke="#8B3A3A" strokeWidth="1.2" strokeLinecap="round" opacity="0.4">
      <animate attributeName="opacity" values="0.2;0.5;0.2" dur="1.5s" repeatCount="indefinite" />
      <animate attributeName="d" values="M20 16 C20 12 24 12 22 8;M20 16 C18 11 23 10 21 6;M20 16 C20 12 24 12 22 8" dur="2s" repeatCount="indefinite" />
    </path>
    <path d="M28 14 C28 10 32 10 30 5" stroke="#8B3A3A" strokeWidth="1.2" strokeLinecap="round" opacity="0.35">
      <animate attributeName="opacity" values="0.15;0.45;0.15" dur="1.8s" repeatCount="indefinite" />
      <animate attributeName="d" values="M28 14 C28 10 32 10 30 5;M28 14 C26 9 31 8 29 3;M28 14 C28 10 32 10 30 5" dur="2.2s" repeatCount="indefinite" />
    </path>
    <path d="M36 16 C36 12 40 12 38 8" stroke="#8B3A3A" strokeWidth="1.2" strokeLinecap="round" opacity="0.3">
      <animate attributeName="opacity" values="0.2;0.5;0.2" dur="1.6s" repeatCount="indefinite" />
      <animate attributeName="d" values="M36 16 C36 12 40 12 38 8;M36 16 C34 11 39 10 37 6;M36 16 C36 12 40 12 38 8" dur="1.9s" repeatCount="indefinite" />
    </path>
    <defs>
      <linearGradient id="cupGrad" x1="8" y1="22" x2="48" y2="50">
        <stop offset="0%" stopColor="#f5e6d3" />
        <stop offset="50%" stopColor="#eedcc8" />
        <stop offset="100%" stopColor="#e0cbb3" />
      </linearGradient>
      <linearGradient id="teaGrad" x1="12" y1="28" x2="44" y2="46">
        <stop offset="0%" stopColor="#c87533" />
        <stop offset="100%" stopColor="#8B3A3A" />
      </linearGradient>
      <linearGradient id="rimGrad" x1="8" y1="18" x2="48" y2="26">
        <stop offset="0%" stopColor="#fff9f0" />
        <stop offset="50%" stopColor="#f5e6d3" />
        <stop offset="100%" stopColor="#e8d5c0" />
      </linearGradient>
    </defs>
  </svg>
);

const products = [
  'jaggeryTea',
  'masalaTea',
  'blackTea',
  'greenTea',
  'lemonTea',
  'tulasiTea',
  'sugarFreeTea',
  'chocolateTea',
  'coffee'
];

const Products: React.FC = () => {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState(false);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Start reveal after a short delay to let the page render
    const timer = setTimeout(() => setRevealed(true), 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div>
      <Helmet>
        <title>Our Tea Products | Aswad Amruttulya - Masala, Jaggery, Tulsi & More</title>
        <meta name="description" content="Explore 10+ premium tea varieties from Aswad Amruttulya — Masala Tea, Jaggery Tea, Tulsi Tea, Lemon Tea, Black Tea & more. Perfect for every mood and moment." />
        <meta property="og:title" content="Tea Products | Aswad Amruttulya" />
        <meta property="og:description" content="Premium tea blends crafted with authentic Indian flavors. Discover our full menu of 10+ varieties." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://aswadamruttulya.com/products" />
      </Helmet>
      <Header />
      <section className="page-section products-page">
        <div className="container">
          <div className="section-head">
            <span>{t('products_heading')}</span>
            <h2>{t('products_title')}</h2>
            <p>{t('products_subtitle')}</p>
          </div>
          <div className="product-grid">
            {products.map((item, index) => (
              <div
                key={item}
                className={`product-card-wrapper ${revealed ? 'revealed' : ''}`}
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div
                  className="tea-cup-loader"
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  <div className="tea-cup-icon">
                    <TeaCupIcon />
                  </div>
                </div>
                <div
                  className="product-card-reveal"
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  <ProductCard product={item} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <FranchiseForm />
      <Footer />
    </div>
  );
};

export default Products;